import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';

import {
  CommissionLedgerEntry,
  PartnerClient,
  PartnerClientNote,
  PartnerDashboard,
  PartnerProfile,
  PartnerSettings,
  PartnerTimelineEntry,
  PartnerOwnerInvitationStatus,
  PartnerOwnerOnboardingSummary,
  ManagedOwnerAccountResult,
  ManagedOwnerRegistrationCompletionResult,
  PayoutRecord,
  PartnerPayoutPage,
  ReferralInvite,
  ReferralLink
} from '@app/core/models';

export interface PartnerClientUpsertPayload {
  clientName: string;
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  notesSummary: string;
  status: PartnerClient['status'];
  onboardingStage: PartnerClient['onboardingStage'];
  referralSource: string;
  commissionOverride?: number | null;
  expectedPropertyCount: number;
  subscriptionStatus: PartnerClient['subscriptionStatus'];
  sendInviteNow?: boolean;
  saveAsLead?: boolean;
}

export interface PartnerInviteCommand {
  action?: 'create';
  partnerClientId?: string | null;
  clientName?: string;
  recipientName?: string;
  recipientEmail?: string;
  message?: string;
}

export interface PartnerInviteActionCommand {
  action: 'resend' | 'cancel';
  inviteId: string;
}

export interface PartnerReferralLinkPayload {
  code?: string;
  slug?: string;
}

export interface PartnerOwnerInvitationPayload {
  partnerClientId?: string | null;
  contactName: string;
  companyName?: string | null;
  email: string;
  phone: string;
  expectedPropertyCount: number;
  productInterest?: string | null;
}

export interface PartnerManagedOwnerPayload {
  registrationSessionToken?: string;
  registrationSessionTokens?: string[];
  contactName: string;
  companyName?: string | null;
  notes?: string | null;
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
  expectedPropertyCount: number;
  grantPmsManagerAccess: boolean;
  authorizationAttested: boolean;
}

@Injectable({ providedIn: 'root' })
export class PartnerApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/partner`;

  getDashboard(startDate?: string, endDate?: string) {
    return this.http.get<PartnerDashboard>(`${this.baseUrl}/dashboard`, { params: this.dateParams(startDate, endDate) });
  }

  getProfile() {
    return this.http.get<PartnerProfile>(`${this.baseUrl}/profile`);
  }

  updateProfile(payload: Partial<PartnerProfile>) {
    return this.http.put<PartnerProfile>(`${this.baseUrl}/profile`, payload);
  }

  getSettings() {
    return this.http.get<PartnerSettings>(`${this.baseUrl}/settings`);
  }

  updateSettings(payload: Partial<PartnerSettings>) {
    return this.http.put<PartnerSettings>(`${this.baseUrl}/settings`, payload);
  }

  removeAchBankAccount() {
    return this.http.delete<PartnerSettings>(`${this.baseUrl}/settings/ach-bank-account`);
  }

  getPayoutPage(status: 'ALL' | 'PENDING' | 'COMPLETED', page = 1, pageSize = 10, startDate?: string, endDate?: string) {
    return this.http.get<PartnerPayoutPage>(`${this.baseUrl}/payouts/paged`, {
      params: { ...this.dateParams(startDate, endDate), status, page, pageSize }
    });
  }

  private dateParams(startDate?: string, endDate?: string): Record<string, string> {
    const params: Record<string, string> = {};
    if (startDate) params['startDate'] = startDate;
    if (endDate) params['endDate'] = endDate;
    return params;
  }

  getClients() {
    return this.http.get<PartnerClient[]>(`${this.baseUrl}/clients`);
  }

  getClient(partnerClientId: string) {
    return this.http.get<PartnerClient>(`${this.baseUrl}/clients/${partnerClientId}`);
  }

  createClient(payload: PartnerClientUpsertPayload) {
    return this.http.post<PartnerClient>(`${this.baseUrl}/clients`, payload);
  }

  updateClient(partnerClientId: string, payload: PartnerClientUpsertPayload) {
    return this.http.put<PartnerClient>(`${this.baseUrl}/clients/${partnerClientId}`, payload);
  }

  getOwnerOnboarding(partnerClientId: string) {
    return this.http.get<PartnerOwnerOnboardingSummary>(
      `${this.baseUrl}/clients/${partnerClientId}/owner-onboarding`
    );
  }

  createOwnerInvitation(payload: PartnerOwnerInvitationPayload, idempotencyKey: string) {
    return this.http.post<PartnerOwnerInvitationStatus>(`${this.baseUrl}/owner-invitations`, payload, {
      headers: this.idempotencyHeaders(idempotencyKey)
    });
  }

  getOwnerInvitation(invitationId: string) {
    return this.http.get<PartnerOwnerInvitationStatus>(`${this.baseUrl}/owner-invitations/${invitationId}`);
  }

  resendOwnerInvitation(invitationId: string, idempotencyKey: string) {
    return this.http.post<PartnerOwnerInvitationStatus>(
      `${this.baseUrl}/owner-invitations/${invitationId}/resend`,
      {},
      { headers: this.idempotencyHeaders(idempotencyKey) }
    );
  }

  revokeOwnerInvitation(invitationId: string, idempotencyKey: string) {
    return this.http.post<PartnerOwnerInvitationStatus>(
      `${this.baseUrl}/owner-invitations/${invitationId}/revoke`,
      {},
      { headers: this.idempotencyHeaders(idempotencyKey) }
    );
  }

  createManagedOwner(payload: PartnerManagedOwnerPayload, idempotencyKey: string) {
    return this.http.post<ManagedOwnerAccountResult>(`${this.baseUrl}/managed-owner-accounts`, payload, {
      headers: this.idempotencyHeaders(idempotencyKey)
    });
  }

  completeManagedOwnerRegistration(partnerClientId: string, idempotencyKey: string) {
    return this.http.post<ManagedOwnerRegistrationCompletionResult>(
      `${this.baseUrl}/managed-owner-accounts/${partnerClientId}/pms/complete`,
      { deferBillingToOwner: true },
      { headers: this.idempotencyHeaders(idempotencyKey) }
    );
  }

  getClientNotes(partnerClientId: string) {
    return this.http.get<PartnerClientNote[]>(`${this.baseUrl}/clients/${partnerClientId}/notes`);
  }

  addClientNote(partnerClientId: string, body: string) {
    return this.http.post<PartnerClientNote>(`${this.baseUrl}/clients/${partnerClientId}/notes`, { body });
  }

  getClientTimeline(partnerClientId: string) {
    return this.http.get<PartnerTimelineEntry[]>(
      `${this.baseUrl}/clients/${partnerClientId}/timeline`
    );
  }

  getInvites() {
    return this.http.get<ReferralInvite[]>(`${this.baseUrl}/invites`);
  }

  createInvite(payload: PartnerInviteCommand) {
    return this.http.post<ReferralInvite>(`${this.baseUrl}/invites`, payload);
  }

  updateInvite(payload: PartnerInviteActionCommand) {
    return this.http.post<ReferralInvite>(`${this.baseUrl}/invites`, payload);
  }

  getReferralLinks() {
    return this.http.get<ReferralLink[]>(`${this.baseUrl}/referral-links`);
  }

  createReferralLink(payload: PartnerReferralLinkPayload) {
    return this.http.post<ReferralLink>(`${this.baseUrl}/referral-links`, payload);
  }

  getCommissions() {
    return this.http.get<CommissionLedgerEntry[]>(`${this.baseUrl}/commissions`);
  }

  getPayouts() {
    return this.http.get<PayoutRecord[]>(`${this.baseUrl}/payouts`);
  }

  requestPayout(amount?: number) {
    return this.http.post<PayoutRecord>(`${this.baseUrl}/payouts`, { amount });
  }

  private idempotencyHeaders(idempotencyKey: string): HttpHeaders {
    return new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
  }
}
